/**
 * LandingStudent — /student route.
 * Faithful port of student.html from the AI-IDE design.
 * Simulators (from app.js) are intentionally omitted per scope decision.
 *
 * CTA: "Try Now" → /auth/student (B2C student auth)
 */
import { Link } from 'react-router-dom';
import LandingShell from '@/components/landing/LandingShell';
import ContactSection from '@/components/landing/ContactSection';
import { LANDING_CTA, PAGE_SOURCE } from '@/lib/landingCTAs';

const CHECK_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-orange)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 3, flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const STUDENT_FEATURES = [
  {
    img: '/landing-v2/assets/student_chat.webp',
    title: 'AI-Native Learning Assistant Tool',
    body: 'Ask any concept. Get a question back — not an answer. You reason, you discover, you remember.',
  },
  {
    img: '/landing-v2/assets/student_quiz.webp',
    title: 'Adaptive Practice Tests',
    body: 'Every question responds to your last answer. Gaps surface instantly. Mastery is confirmed before you move on.',
  },
  {
    img: '/landing-v2/assets/student_syllabus.webp',
    title: 'Syllabus Navigator',
    body: 'Upload your notes or textbook. Get an NCERT-mapped breakdown — and a session in Socratic Mode on any chapter, instantly.',
  },
];

const STUDENT_BULLETS = [
  {
    title: 'Socratic AI Mentorship',
    desc: 'Receive guiding questions instead of direct answers, training your brain to think critically.',
  },
  {
    title: 'Adaptive Check-ins',
    desc: 'Quizzes automatically adapt difficulty based on your answers to target and resolve learning gaps.',
  },
  {
    title: 'NCERT Syllabus Navigator',
    desc: 'Map notes and textbooks directly to board requirements, ensuring you focus on what matters most.',
  },
];

export default function LandingStudent() {
  return (
    <LandingShell activeNav="student">
      {/* ============== HERO ============== */}
      <section id="hero" style={{ padding: '130px 0 24px 0' }}>
        <div className="container text-center">
          <div className="nep-badge" style={{ margin: '0 auto 16px auto' }}>
            <span className="pulse-dot" /> FOR CLASS 6–12 STUDENTS
          </div>
          <h1 style={{ fontSize: '3rem', marginBottom: 12 }}>
            Think It Through.<br />
            <span className="gradient-text">Don&apos;t Just Look It Up.</span>
          </h1>
          <p className="hero-subtitle" style={{ margin: '0 auto', maxWidth: 520, fontSize: '1.05rem' }}>
            Your AI mentor that asks before it answers — building the kind of
            understanding no exam can shake.
          </p>
          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to={LANDING_CTA.TRY_NOW_STUDENT}
              className="btn btn-primary btn-lg glow-btn"
              data-testid="student-try-now"
            >
              Try Now
            </Link>
          </div>
        </div>
      </section>

      {/* ============== THREE PILLARS ============== */}
      <section className="section-padding light-bg" style={{ padding: '48px 0' }}>
        <div className="container">
          <div className="section-header text-center" style={{ marginBottom: 32 }}>
            <span className="section-tag">WHAT YOU GET</span>
            <h2>Three Tools. One Goal.</h2>
          </div>
          <div className="philosophy-grid">
            {STUDENT_FEATURES.map((f) => (
              <div className="philosophy-card text-center" key={f.title}>
                <img
                  src={f.img}
                  alt={f.title}
                  style={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 12, marginBottom: 20 }}
                />
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== PORTAL OVERVIEW ============== */}
      <section id="demo" className="section-padding" style={{ padding: '64px 0 80px 0' }}>
        <div className="container">
          <div className="panel-layout">
            <div className="vertical-pitch">
              <div className="pitch-tag">STUDENT PORTAL</div>
              <h3>Designed for active learning</h3>
              <p className="pitch-lead">
                Yukti turns study time from passive reading into active, engaging
                dialogue. By encouraging you to think through concepts yourself,
                it builds deep understanding that no exam can shake.
              </p>
              <ul
                className="pitch-bullet-list"
                style={{ marginTop: 16, listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 16 }}
              >
                {STUDENT_BULLETS.map((b) => (
                  <li key={b.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {CHECK_ICON}
                    <div>
                      <strong style={{ display: 'block', color: 'var(--text-primary)', marginBottom: 2 }}>
                        {b.title}
                      </strong>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                        {b.desc}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="showcase-card glass"
              style={{
                overflow: 'hidden',
                borderRadius: 20,
                border: '1px solid var(--border-color)',
                boxShadow: 'var(--shadow-card)',
                backgroundColor: 'var(--bg-tertiary)',
              }}
            >
              <img
                src="/landing-v2/assets/confident_student.webp"
                alt="Student achieving concept mastery on Yukti"
                style={{ width: '100%', height: 340, objectFit: 'cover', display: 'block' }}
              />
              <div
                className="showcase-caption"
                style={{ padding: 24, background: 'var(--bg-primary)', borderTop: '1px solid var(--border-color)' }}
              >
                <h4 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', color: 'var(--text-primary)' }}>
                  Concept Mastered
                </h4>
                <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  Kavita solved fluid mechanics through guided Socratic inquiry —
                  learning by thinking, not just memorising formulas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactSection sourcePage={PAGE_SOURCE.STUDENT} />
    </LandingShell>
  );
}
